export interface IImageTransformConfig {
  w?: number
  h?: number
  quality?: number
  removeTag?: boolean
}

const imageProcessHosts = new Set([
  'image.kilitest.com',
  'image.kilimall.com',
  'img.kilimall.com',
  'mkp-cn-testing.obs.cn-south-1.myhuaweicloud.com',
  'mkp.obs.af-south-1.myhuaweicloud.com'
])
const signedParameter = /^(?:signature|accesskeyid|awsaccesskeyid|ossaccesskeyid|policy|auth_key|token|x-(?:amz|obs)-.+)$/i
const isPositiveInteger = (value?: number): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value > 0

/** Merge only our image-service operations; retain unrelated query bytes and fragments. */
export const transformImageUrl = (
  source: string,
  config: IImageTransformConfig = {},
  supportsWebp: boolean,
  configuredHost?: string
): string => {
  if (!source || !/^https?:\/\//i.test(source)) return source

  let parsed: URL
  try {
    parsed = new URL(source)
  } catch {
    return source
  }
  if (parsed.username || parsed.password) return source
  if (!imageProcessHosts.has(parsed.hostname) && parsed.hostname !== configuredHost) return source
  for (const key of parsed.searchParams.keys()) {
    if (signedParameter.test(key)) return source
  }

  const hashIndex = source.indexOf('#')
  const fragment = hashIndex < 0 ? '' : source.slice(hashIndex)
  const hash = config.removeTag && fragment === '#' ? '' : fragment
  const withoutHash = hashIndex < 0 ? source : source.slice(0, hashIndex)
  const queryIndex = withoutHash.indexOf('?')
  let base = queryIndex < 0 ? withoutHash : withoutHash.slice(0, queryIndex)
  const parameters = queryIndex < 0 ? [] : withoutHash.slice(queryIndex + 1).split('&')
  const processIndexes: number[] = []
  let processValue = ''
  try {
    parameters.forEach((parameter, index) => {
      const equals = parameter.indexOf('=')
      const key = decodeURIComponent(equals < 0 ? parameter : parameter.slice(0, equals))
      if (key === 'x-image-process') {
        processIndexes.push(index)
        processValue = decodeURIComponent(equals < 0 ? '' : parameter.slice(equals + 1))
      }
    })
  } catch {
    return source
  }
  // Ambiguous or foreign processing pipelines must keep their original semantics.
  if (processIndexes.length > 1 || (processIndexes.length && !/^image(?:\/|$)/.test(processValue))) return source

  let operations = processIndexes.length ? processValue.split('/').slice(1) : []
  if (!supportsWebp) {
    operations = operations.filter(operation => !/^format,webp$/i.test(operation))
    // Legacy generated filenames have an original JPG/PNG/GIF sibling; native .webp does not.
    base = base.replace(/(\.(?:jpe?g|png|gif))\.webp$/i, '$1')
  } else if (!processIndexes.length) {
    operations.push('format,webp')
  }

  const width = isPositiveInteger(config.w) ? config.w : undefined
  const height = isPositiveInteger(config.h) ? config.h : undefined
  if (width || height) {
    const resizeIndexes = operations.flatMap((operation, index) => operation.startsWith('resize,') ? [index] : [])
    const first = resizeIndexes[0]
    if (resizeIndexes.length > 1) {
      const simpleAndAdjacent = resizeIndexes.every((index, offset) =>
        index === first + offset && /^resize,(?:[wh]_\d+)(?:,[wh]_\d+)*$/.test(operations[index]))
      if (!simpleAndAdjacent) return source
    }
    const values: string[] = []
    for (const index of resizeIndexes) {
      for (const value of operations[index].split(',').slice(1)) {
        const previous = values.findIndex(item => item.split('_')[0] === value.split('_')[0])
        if (previous < 0) values.push(value)
        else values[previous] = value
      }
    }
    for (const [axis, size] of [['w', width], ['h', height]] as const) {
      if (!size) continue
      const previous = values.findIndex(value => value.startsWith(axis + '_'))
      if (previous < 0) values.push(`${axis}_${size}`)
      else values[previous] = `${axis}_${size}`
    }
    const resize = 'resize,' + values.join(',')
    if (resizeIndexes.length) operations.splice(first, resizeIndexes.length, resize)
    else operations.push(resize)
  }

  if (isPositiveInteger(config.quality) && config.quality <= 100) {
    const index = operations.findIndex(operation => operation.startsWith('quality,'))
    const quality = `quality,q_${config.quality}`
    if (index < 0) operations.push(quality)
    else operations[index] = quality
  }

  const processIndex = processIndexes[0]
  if (operations.length) {
    const parameter = 'x-image-process=image/' + operations.join('/')
    if (processIndexes.length) parameters[processIndex] = parameter
    else parameters.push(parameter)
  } else if (processIndexes.length) {
    parameters.splice(processIndex, 1)
  }
  const query = parameters.length ? '?' + parameters.join('&') : ''
  return base + query + hash
}
